<script setup lang="ts">
import { tpgIconChevronLeft, tpgIconChevronRight } from '@tpg/grammar-web-icons';
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarModal, GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';

import type { GalleryFile } from '#ui-shared/components/gallery/gallery.types';
import {
  getGalleryFileSource,
  getInlineGalleryFileUrl,
} from '#ui-shared/components/gallery/gallery.types';
import GalleryMedia from '#ui-shared/components/gallery/GalleryMedia.vue';
import GallerySideBar from '#ui-shared/components/gallery/GallerySideBar.vue';

interface GalleryProps {
  file: GalleryFile;
  refreshUrls?: () => void;
  fileKey?: string;
  canPrevious?: boolean;
  canNext?: boolean;
}

const props = defineProps<GalleryProps>();

const emit = defineEmits<{
  close: [];
  previous: [];
  next: [];
}>();

const { t } = useI18n();
const isOpen = ref(false);
/**
 * Grammar clears body overflow when its modal closes, even if a parent modal is still open.
 * Save the existing value before opening the gallery so we can restore the parent's scroll lock.
 */
const previousOverflow = document.body.style.overflow;
const source = computed(() => getGalleryFileSource(props.file));
const inlineUrl = computed(() => getInlineGalleryFileUrl(props.file));
const hasNavigation = computed(() => props.canPrevious || props.canNext);

onMounted(async () => {
  // Grammar ignores isOpen on its first render.
  await nextTick();
  isOpen.value = true;
});
onBeforeUnmount(() => {
  // Restore the original scroll state on unmount
  document.body.style.overflow = previousOverflow;
});

/**
 * Navigates with the arrow keys, unless a video has focus and uses them to seek.
 */
function onKeydown(event: KeyboardEvent): void {
  if (event.target instanceof HTMLVideoElement) {
    return;
  }

  if (event.key === 'ArrowLeft' && props.canPrevious) {
    emit('previous');
  }
  else if (event.key === 'ArrowRight' && props.canNext) {
    emit('next');
  }
}

function openInNewWindow(): void {
  if (inlineUrl.value) {
    window.open(inlineUrl.value, '_blank', 'noopener,noreferrer');
  }
}

/** Let Grammar finish releasing its scroll lock before unmount restores the parent lock. */
async function onClose(): Promise<void> {
  await nextTick();
  emit('close');
}
</script>

<template>
  <GrammarModal
    :is-open="isOpen && Boolean(source)"
    :aria-label="`Preview ${file.fileName}`"
    @close="onClose"
    @keydown="onKeydown"
  >
    <template #content>
      <div
        v-if="source"
        class="fixed inset-0 bg-[#0A0A0A]/85 flex items-center justify-center"
        @click.self="isOpen = false"
      >
        <GallerySideBar
          :file="file"
          @close="isOpen = false"
        />

        <GalleryMedia
          :key="fileKey ?? file.fileName"
          :file="file"
          :refresh-urls="refreshUrls"
          @open="openInNewWindow"
          @close="isOpen = false"
        />

        <template v-if="hasNavigation">
          <GrammarQuaternaryButton
            class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60"
            :icon-template="tpgIconChevronLeft"
            :aria-label="t('gallery.previous')"
            :disabled="!canPrevious"
            icon-color="white"
            size="overlay"
            @click="emit('previous')"
          />
          <GrammarQuaternaryButton
            class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60"
            :icon-template="tpgIconChevronRight"
            :aria-label="t('gallery.next')"
            :disabled="!canNext"
            icon-color="white"
            size="overlay"
            @click="emit('next')"
          />
        </template>
      </div>
    </template>
  </GrammarModal>
</template>
